import { useSocket } from "@/contexts/SocketContext.tsx";
import { badgeQueries } from "@/hooks/useBadge.ts";
import { useThrottledInvalidation } from "@/shared/useThrottledInvalidation.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import type { ServerToClientThreadEvents } from "@/types/socketIo.ts";
import { affectsThreadBadge } from "@/types/thread.ts";

/** Longer than the shared 2s default — an ambient count tolerates more lag than the thread list. */
export const BADGE_INVALIDATE_THROTTLE_MS = 5000;

/** Refetches allowed before the window engages, so ordinary single events stay instant. */
export const BADGE_INVALIDATE_BURST_ALLOWANCE = 3;

/**
 * Keep the awareness badge live: the badge doesn't poll, so socket activity is
 * what refreshes it. `thread:status_update` is chatty and covers the transitions
 * with no terminal event — notably the user replying and clearing
 * `waitingForUser` — while the dedicated endpoint and shared throttle keep the
 * resulting refetch rate bounded.
 *
 * Persisted `thread:mutated` events refresh only when a field used by the badge
 * predicate changes. They complement rather than replace the broader signals:
 * several badge-relevant writes do not emit a durable mutation yet.
 */
export const useBadgeRefreshSocket = () => {
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  const pendingRefresh = useRef(false);
  const hasConnected = useRef(false);

  const flush = useCallback(() => {
    if (!pendingRefresh.current) return false;
    pendingRefresh.current = false;
    return queryClient.invalidateQueries({ queryKey: badgeQueries.all() });
  }, [queryClient]);
  const { trigger, cancelPending } = useThrottledInvalidation(flush, {
    throttleMs: BADGE_INVALIDATE_THROTTLE_MS,
    burstAllowance: BADGE_INVALIDATE_BURST_ALLOWANCE,
  });

  const handleBadgeChanged = useCallback(() => {
    pendingRefresh.current = true;
    trigger();
  }, [trigger]);

  const handleThreadMutated = useCallback(
    (eventData: Parameters<ServerToClientThreadEvents["thread:mutated"]>[0]) => {
      const { diff } = eventData;
      if (!diff || !affectsThreadBadge(diff)) return;
      handleBadgeChanged();
    },
    [handleBadgeChanged],
  );

  useEffect(() => {
    if (!socket) return;

    // Socket.IO does not replay events missed during an outage, so every
    // reconnection must reconcile canonical badge state. The first connection
    // stays quiet because the mounted badge query performs its own initial read.
    if (hasConnected.current) pendingRefresh.current = true;
    hasConnected.current = true;

    // Also drain a trailing refresh cancelled by the disconnect cleanup.
    if (pendingRefresh.current) trigger();

    socket.on("thread:notify", handleBadgeChanged);
    socket.on("thread:status_update", handleBadgeChanged);
    socket.on("thread:mutated", handleThreadMutated);
    // A deleted thread takes its unread and needs-attention rows with it. The
    // tab that did the deleting refreshes the badge from the mutation, but every
    // other tab and device only learns through this socket — and the badge does
    // not poll, so without this they keep a count pointing at nothing until
    // something unrelated happens to fire.
    socket.on("thread:deleted", handleBadgeChanged);
    return () => {
      socket.off("thread:notify", handleBadgeChanged);
      socket.off("thread:status_update", handleBadgeChanged);
      socket.off("thread:mutated", handleThreadMutated);
      socket.off("thread:deleted", handleBadgeChanged);
      // Keep `pendingRefresh` set so reconnect drains the cancelled work.
      cancelPending();
    };
  }, [socket, handleBadgeChanged, handleThreadMutated, cancelPending, trigger]);
};
